import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';

import EnhancedTable from '#components/EnhancedTable';
import type { EnhancedTableProps } from '#components/EnhancedTable';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import {
  useGetCouponsMutation,
  useDeleteCouponMutation,
  useUpdateCouponStatusMutation,
} from '#services/coupon';
import { formatCurrency, formatDate } from '#common/utils';
import { KEY_CODES } from '#utils/constants';
import {
  COUPON_STATUS,
  COUPON_DISCOUNT_TYPES,
  COUPON_TARGET_TYPES,
} from '#common/constants';

type SortOrder = 'asc' | 'desc';

interface CouponRow {
  id?: string | number;
  code?: string;
  name?: string;
  status?: string;
  startDate?: string | number | Date;
  endDate?: string | number | Date;
  discountType?: string;
  discountValue?: number;
  targetType?: string;
  targetIds?: unknown[];
  assignmentType?: string;
  assignedCustomerIds?: unknown[];
  useCount?: number;
  totalSavings?: number;
  [key: string]: unknown;
}

interface CouponsResponse {
  error?: unknown;
  rows?: CouponRow[];
  total?: number;
  data?: { rows?: CouponRow[]; total?: number };
}

function getStatusChip(coupon: CouponRow) {
  const now = new Date();
  const startDate = new Date(coupon.startDate as string | number | Date);
  const endDate = new Date(coupon.endDate as string | number | Date);

  if (coupon.status === COUPON_STATUS.DISABLED) {
    return <Chip size="small" label="Disabled" color="error" />;
  }
  if (now < startDate) {
    return <Chip size="small" label="Scheduled" color="info" />;
  }
  if (now > endDate) {
    return <Chip size="small" label="Expired" color="default" />;
  }
  return <Chip size="small" label="Active" color="success" />;
}

function getDiscountText(coupon: CouponRow) {
  if (coupon.discountType === COUPON_DISCOUNT_TYPES.PERCENT) {
    return `${coupon.discountValue}% off`;
  }
  return `${formatCurrency(coupon.discountValue as number)} off`;
}

function getTargetText(coupon: CouponRow) {
  if (coupon.targetType === COUPON_TARGET_TYPES.CART) {
    return 'Entire Cart';
  }
  if (coupon.targetType === COUPON_TARGET_TYPES.COLLECTION) {
    return `${coupon.targetIds?.length || 0} Collection(s)`;
  }
  return `${coupon.targetIds?.length || 0} Product(s)`;
}

export default function CouponList() {
  const {
    showBackdrop,
    hideBackdrop,
    showDialog,
    hideDialog,
    setDialogData,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const navigate = useNavigate();
  const [getCoupons] = useGetCouponsMutation();
  const [deleteCoupon] = useDeleteCouponMutation();
  const [updateCouponStatus] = useUpdateCouponStatusMutation();

  const [filter, setFilter] = useState('');
  const [filterForQuery, setFilterForQuery] = useState('');
  const [order, setOrder] = useState<SortOrder>('desc');
  const [orderBy, setOrderBy] = useState('createdDate');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(10);
  const [rows, setRows] = useState<CouponRow[]>([]);
  const [rowCount, setRowCount] = useState(0);

  const fetchCoupons = () => {
    showBackdrop();
    void getCoupons({
      order,
      orderBy,
      filter: filterForQuery,
      page: pageIndex,
      pageSize,
    }).then((res) => {
      const data = ('data' in res ? res.data : undefined) as CouponsResponse | undefined;
      if (data && !data.error) {
        const nextRows = data.data?.rows || data.rows || [];
        const total = data.data?.total || data.total || 0;
        setRows(nextRows);
        setRowCount(total);
      } else {
        simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
      }
    }).finally(() => {
      hideBackdrop();
    });
  };

  useEffect(() => {
    fetchCoupons();
  }, [order, orderBy, filterForQuery, pageIndex, pageSize]);

  const handleToggleStatus = (coupon: CouponRow) => {
    const newStatus = coupon.status === COUPON_STATUS.ACTIVE
      ? COUPON_STATUS.DISABLED
      : COUPON_STATUS.ACTIVE;

    showBackdrop();
    void updateCouponStatus({ id: coupon.id, status: newStatus })
      .then((res) => {
        const action = newStatus === COUPON_STATUS.ACTIVE ? 'enabled' : 'disabled';
        simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], `Coupon "${coupon.code}" was ${action}.`);
        fetchCoupons();
      })
      .finally(() => {
        hideBackdrop();
      });
  };

  const handleDelete = (coupon: CouponRow) => {
    setDialogData({
      title: 'Delete Coupon',
      text: `Are you sure you want to delete coupon "${coupon.code}"?`,
      disagree: 'Cancel',
      agree: 'Delete',
      onDisagree: () => hideDialog(),
      onAgree: () => {
        hideDialog();
        showBackdrop();

        void deleteCoupon({ id: coupon.id })
          .then((res) => {
            simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], `Coupon "${coupon.code}" was deleted.`);
            fetchCoupons();
          })
          .finally(() => {
            hideBackdrop();
          });
      },
    });
    showDialog();
  };

  const tableHeaders: EnhancedTableProps['headers'] = [
    {
      id: 'code',
      label: 'Code',
      renderer: (row) => (
        <Link
          component="button"
          onClick={() => { void navigate(`/management/coupon/edit?id=${(row as CouponRow).id}`); }}
          sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
        >
          {(row as CouponRow).code}
        </Link>
      ),
    },
    {
      id: 'name',
      label: 'Name',
    },
    {
      id: 'discountValue',
      label: 'Discount',
      renderer: (row) => getDiscountText(row),
    },
    {
      id: 'targetType',
      label: 'Target',
      renderer: (row) => getTargetText(row),
    },
    {
      id: 'startDate',
      label: 'Valid Period',
      renderer: (row) => (
        <Box sx={{ fontSize: '0.85rem' }}>
          {formatDate((row as CouponRow).startDate as string | number | Date)} - {formatDate((row as CouponRow).endDate as string | number | Date)}
        </Box>
      ),
    },
    {
      id: 'assignmentType',
      label: 'Assignment',
      renderer: (row) => (
        (row as CouponRow).assignmentType === 'ALL'
          ? <Chip size="small" label="All Customers" variant="outlined" />
          : <Chip size="small" label={`${(row as CouponRow).assignedCustomerIds?.length || 0} Customer(s)`} variant="outlined" />
      ),
    },
    {
      id: 'useCount',
      label: 'Uses',
      renderer: (row) => (row as CouponRow).useCount ?? 0,
    },
    {
      id: 'totalSavings',
      label: 'Total Savings',
      renderer: (row) => (((row as CouponRow).totalSavings || 0) > 0 ? formatCurrency((row as CouponRow).totalSavings as number) : '—'),
    },
    {
      id: 'status',
      label: 'Status',
      renderer: (row) => getStatusChip(row),
    },
    {
      id: 'action',
      label: 'Actions',
      sortable: false,
      renderer: (row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Link
            component="button"
            onClick={() => { void navigate(`/management/coupon/edit?id=${(row as CouponRow).id}`); }}
          >
            Edit
          </Link>
          <Link
            component="button"
            onClick={() => handleToggleStatus(row)}
          >
            {(row as CouponRow).status === COUPON_STATUS.ACTIVE ? 'Disable' : 'Enable'}
          </Link>
          <Link
            component="button"
            onClick={() => handleDelete(row)}
            sx={{ color: 'error.main' }}
          >
            Delete
          </Link>
        </Box>
      ),
    },
  ];

  const tableProps: EnhancedTableProps = {
    name: 'coupons',
    headers: tableHeaders,
    enableSort: true,
    order,
    setOrder: (val) => setOrder(val),
    orderBy,
    setOrderBy: (val) => setOrderBy(val),
    pageSize,
    pageIndex,
    setPageIndex: (val) => setPageIndex(val),
    rowCount,
    rows,
  };

  return (
    <>
      <Box sx={{ typography: 'h7', mb: 2 }}>
        Coupons
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Paper
          elevation={0}
          variant="outlined"
          square
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: 400,
          }}
        >
          <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
            <SearchIcon />
          </IconButton>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Search by code or name"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyUp={(e) => {
              if (e.keyCode === KEY_CODES.ENTER || !filter.length) {
                setFilterForQuery(filter);
              }
            }}
          />
        </Paper>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { void navigate('/management/coupon/edit'); }}
        >
          Create Coupon
        </Button>
      </Box>

      <EnhancedTable {...tableProps} />
    </>
  );
}
